<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div
    @click="toggleSelection"
    class="relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200"
    :class="
      isSelected
        ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
        : 'border-[var(--color-primary-400)] bg-[var(--color-primary-100)] hover:bg-[var(--color-primary-200)]'
    ">
    <!-- Selection Indicator -->
    <div
      class="absolute top-2 right-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200"
      :class="
        isSelected
          ? 'bg-green-500 border-green-500'
          : 'bg-transparent border-[var(--color-primary-400)]'
      ">
      <svg
        v-if="isSelected"
        class="w-4 h-4 text-white"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="3"
          d="M5 13l4 4L19 7"></path>
      </svg>
    </div>

    <!-- Goat Info -->
    <div class="pr-8">
      <div class="flex items-center gap-2 mb-1">
        <span class="text-lg"></span>
        <h3 class="font-bold text-[var(--color-text)] truncate">
          {{ goat.name }}
        </h3>
      </div>
      <div
        class="flex items-center gap-2 text-xs text-[var(--color-text)] opacity-70">
        <span class="font-mono">ID: {{ goat.id }}</span>
        <span
          v-if="goat.gender"
          class="inline-flex items-center px-2 py-0.5 rounded-full bg-[var(--color-primary-200)]">
          {{ goat.gender === 'Male' ? '' : '' }} {{ goat.gender }}
        </span>
      </div>
      <div
        v-if="goat.breed"
        class="text-xs text-[var(--color-text)] opacity-60 mt-1 truncate">
        {{ goat.breed }}
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed } from 'vue'

  const props = defineProps({
    goat: {
      type: Object,
      required: true,
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
  })

  const emit = defineEmits(['update:modelValue'])

  const isSelected = computed(() => props.modelValue)

  function toggleSelection() {
    emit('update:modelValue', !isSelected.value)
  }
</script>
